// relogin-screen.component.ts
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';

export interface ReloginStep {
  id: number;
  label: string;
  status: 'pending' | 'active' | 'done';
}

@Component({
  selector: 'app-relogin-screen',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="relogin-wrap">

      <!-- Blobs de fondo -->
      <div class="bg-blob" style="width:300px;height:300px;background:#10b981;top:-80px;left:-80px;"></div>
      <div class="bg-blob" style="width:250px;height:250px;background:#6366f1;bottom:-60px;right:-60px;"></div>
      <div class="bg-blob" style="width:200px;height:200px;background:#0ea5e9;top:50%;left:60%;transform:translate(-50%,-50%);"></div>

      <!-- Logo + Título -->
      <div class="logo-zone">
        <div class="orbit-system">
          <div class="orbit-core">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
              stroke="white" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </div>
          <div class="orbit-dot dot-1"></div>
          <div class="orbit-dot dot-2"></div>
          <div class="orbit-dot dot-3"></div>
        </div>

        <p class="title-text">{{ title }}</p>
        <p class="sub-text">{{ subtitle }}</p>
      </div>

      <!-- Barra de progreso -->
      <div class="progress-zone">
        <div class="progress-track">
          <div class="progress-fill" [style.width.%]="progress"></div>
        </div>
        <div class="progress-label">
          <span>{{ currentStepLabel }}</span>
          <span>{{ progress }}%</span>
        </div>
      </div>

      <!-- Pasos -->
      <div class="steps-zone">
        <div
          *ngFor="let step of steps"
          class="step-item"
          [class.active]="step.status === 'active'"
          [class.done]="step.status === 'done'">
          <div class="step-icon">
            <ng-container [ngSwitch]="step.status">
              <span *ngSwitchCase="'done'">✓</span>
              <div *ngSwitchCase="'active'" class="spinner-icon"></div>
              <span *ngSwitchDefault>{{ step.id }}</span>
            </ng-container>
          </div>
          <span>{{ step.label }}</span>
        </div>
      </div>

    </div>
  `,
  styles: [`
    @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
    @keyframes pulse-ring { 0%{transform:scale(0.8);opacity:0.8} 100%{transform:scale(1.6);opacity:0} }
    @keyframes spin { to{transform:rotate(360deg)} }
    @keyframes shimmer { 0%{background-position:-200% 0} 100%{background-position:200% 0} }
    @keyframes fadeUp { from{opacity:0;transform:translateY(16px)} to{opacity:1;transform:translateY(0)} }
    @keyframes orbit  { from{transform:rotate(0deg)   translateX(36px) rotate(0deg)}   to{transform:rotate(360deg)  translateX(36px) rotate(-360deg)} }
    @keyframes orbit2 { from{transform:rotate(120deg) translateX(36px) rotate(-120deg)} to{transform:rotate(480deg)  translateX(36px) rotate(-480deg)} }
    @keyframes orbit3 { from{transform:rotate(240deg) translateX(36px) rotate(-240deg)} to{transform:rotate(600deg)  translateX(36px) rotate(-600deg)} }

    .relogin-wrap {
      position: fixed; inset: 0; z-index: 1000;
      display: flex; flex-direction: column;
      align-items: center; justify-content: center;
      gap: 2.5rem; padding: 2rem;
      background: #f0fdf8;
      overflow: hidden;
    }

    .bg-blob {
      position: absolute; border-radius: 50%;
      filter: blur(60px); opacity: 0.12;
      pointer-events: none;
    }

    .logo-zone {
      animation: fadeUp 0.6s ease both;
      display: flex; flex-direction: column;
      align-items: center; gap: 1rem; z-index: 1;
    }

    .orbit-system {
      position: relative; width: 96px; height: 96px;
      display: flex; align-items: center; justify-content: center;
    }

    .orbit-core {
      width: 40px; height: 40px; border-radius: 50%;
      background: linear-gradient(135deg, #10b981, #0ea5e9);
      display: flex; align-items: center; justify-content: center;
      position: relative; z-index: 2;
    }

    .orbit-core::after {
      content: ''; position: absolute; inset: -6px;
      border-radius: 50%; border: 1.5px solid rgba(16,185,129,0.25);
      animation: pulse-ring 2s ease-out infinite;
    }

    .orbit-dot {
      position: absolute; width: 10px; height: 10px;
      border-radius: 50%;
      top: 50%; left: 50%; margin: -5px 0 0 -5px;
    }

    .dot-1 { background: #10b981; animation: orbit  1.8s linear infinite; }
    .dot-2 { background: #0ea5e9; animation: orbit2 1.8s linear infinite; }
    .dot-3 { background: #6366f1; animation: orbit3 1.8s linear infinite; }

    .title-text {
      font-size: 22px; font-weight: 500;
      color: #111827; letter-spacing: -0.3px;
    }

    .sub-text {
      font-size: 14px; color: #6b7280;
      text-align: center; max-width: 280px; line-height: 1.6;
    }

    .progress-zone {
      animation: fadeUp 0.6s 0.2s ease both;
      z-index: 1; width: 100%; max-width: 320px;
      display: flex; flex-direction: column; gap: 10px;
    }

    .progress-track {
      height: 4px; background: rgba(0,0,0,0.08);
      border-radius: 99px; overflow: hidden;
    }

    .progress-fill {
      height: 100%; border-radius: 99px;
      background: linear-gradient(90deg, #10b981, #0ea5e9, #6366f1);
      background-size: 200% 100%;
      animation: shimmer 2s linear infinite;
      transition: width 0.6s cubic-bezier(.4,0,.2,1);
    }

    .progress-label {
      display: flex; justify-content: space-between;
      font-size: 12px; color: #6b7280;
    }

    .steps-zone {
      animation: fadeUp 0.6s 0.4s ease both;
      z-index: 1; display: flex; flex-direction: column;
      gap: 8px; width: 100%; max-width: 320px;
    }

    .step-item {
      display: flex; align-items: center; gap: 10px;
      padding: 10px 14px; border-radius: 8px;
      border: 0.5px solid rgba(0,0,0,0.08);
      background: white; font-size: 13px; color: #9ca3af;
      transition: all 0.3s ease;
    }

    .step-item.done  { border-color: rgba(16,185,129,0.3); background: rgba(16,185,129,0.04); color: #111827; }
    .step-item.active{ border-color: rgba(14,165,233,0.4); background: rgba(14,165,233,0.05); color: #111827; }

    .step-icon {
      width: 20px; height: 20px; border-radius: 50%;
      display: flex; align-items: center; justify-content: center;
      flex-shrink: 0; font-size: 10px; font-weight: 500;
      border: 1.5px solid rgba(0,0,0,0.12); color: #9ca3af;
    }

    .step-item.done  .step-icon { background: #10b981; border-color: #10b981; color: white; }
    .step-item.active .step-icon{ border-color: #0ea5e9; color: #0ea5e9; }

    .spinner-icon {
      width: 14px; height: 14px; border-radius: 50%;
      border: 1.5px solid #0ea5e9; border-top-color: transparent;
      animation: spin 0.8s linear infinite;
    }
  `]
})
export class ReloginScreenComponent implements OnInit, OnDestroy {

  constructor(private cdr: ChangeDetectorRef) { }
  @Input() logoUrl = 'assets/img/logo-GISECloud-vertical-dark.png';

  title = 'Restaurando sesión';
  subtitle = 'Verificando credenciales y cargando tu espacio de trabajo…';
  progress = 0;
  currentStepLabel = 'Iniciando…';

  steps: ReloginStep[] = [
    { id: 1, label: 'Verificando token de sesión', status: 'pending' },
    { id: 2, label: 'Cargando datos del usuario', status: 'pending' },
    { id: 3, label: 'Preparando el entorno', status: 'pending' },
    { id: 4, label: 'Redirigiendo…', status: 'pending' },
  ];

  private progressInterval: any;

  ngOnInit(): void {
    this.activateStep(0);
  }

  ngOnDestroy(): void {
    clearInterval(this.progressInterval);
  }

  // ─── API pública — llama desde LoginBaseComponent ───
nextStep(): void {
  const current = this.steps.findIndex(s => s.status === 'active');
  if (current === -1) return;

  // marcar como done
  this.steps[current] = { ...this.steps[current], status: 'done' };

  // activar siguiente
  if (current + 1 < this.steps.length) {
    this.activateStep(current + 1);
  } else {
    this.completeAll();
  }
}

  completeAll(): void {
    this.steps = this.steps.map(s => ({ ...s, status: 'done' }));
    this.progress = 100;
    this.title = '¡Bienvenido de nuevo!';
    this.subtitle = 'Sesión restaurada correctamente.';
    this.currentStepLabel = 'Completado';
  }


  // ─── Privados ───────────────────────────────────────

  private activateStep(index: number): void {
    const stepProgressMap = [20, 55, 80, 100];
    this.steps[index].status = 'active';
    this.progress = stepProgressMap[index];
    this.currentStepLabel = this.steps[index].label;
  }

  private completeStep(index: number): void {
    this.steps[index].status = 'done';
  }
}
